import { useNavigate } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { SectionCard } from "./identityForm";
import { useAuth } from "../context/AuthContext";
import { track } from "../lib/usageTracker";
import { DELETE_CARD_BODY, DELETE_CARD_TITLE, DELETE_VERB } from "../lib/deleteAccount";

/**
 * The way in to deleting an account (#1032, D204.2): the last card on
 * Settings, after "What we measure" and before the build line, for a player
 * and a coach alike.
 *
 * It deletes nothing. The trigger only navigates to `/settings/delete-account`,
 * the full-page step that says what goes, what stays and when before it asks
 * for anything (D191.1). Alert-outlined, never filled (D55), and never gold:
 * Settings' one gold stays Accept (D186.7, D195).
 */
export default function DeleteAccountCard({ appearance } = {}) {
  const nav = useNavigate();
  const { user } = useAuth();
  // #1594: pressing Delete account is the card's "open" (the card has no fold); the next screen is counted as a view.
  const openDelete = () => {
    track("settings.fold_open", { item: "delete_account" });
    nav("/settings/delete-account");
  };
  // The shared demo accounts cannot be deleted (the API refuses with 403),
  // so the way in is not offered to a demo session.
  if (user?.is_demo) return null;
  // #1135: inside Settings' More it is one alert-outlined button, as the
  // approved boards draw it, and the verb is the board's `Delete account`
  // (I6). The step page's own verb is unchanged.
  if (appearance === "r5") {
    return (
      <button
        type="button"
        onClick={openDelete}
        data-testid="settings-delete-account"
        style={{
          alignSelf: "flex-start",
          minHeight: 44,
          padding: "0 14px",
          background: "var(--nw-surface)",
          border: "1px solid var(--nw-alert)",
          color: "var(--nw-alert)",
          fontWeight: 600,
          fontSize: 14,
          borderRadius: 10,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          cursor: "pointer",
        }}
      >
        <Trash2 size={16} aria-hidden="true" />
        Delete account
      </button>
    );
  }
  return (
    <SectionCard
      icon={Trash2}
      title={DELETE_CARD_TITLE}
      desc={DELETE_CARD_BODY}
      testid="settings-delete-account-card"
      appearance={appearance}
    >
      <button
        type="button"
        onClick={openDelete}
        data-testid="settings-delete-account"
        style={{
          alignSelf: "flex-start",
          minHeight: 40,
          padding: "0 14px",
          background: "#FFFFFF",
          border: "1px solid var(--nw-alert)",
          color: "var(--nw-alert)",
          fontWeight: 600,
          fontSize: 14,
          borderRadius: 10,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          cursor: "pointer",
        }}
      >
        <Trash2 size={16} aria-hidden="true" />
        {DELETE_VERB}
      </button>
    </SectionCard>
  );
}
